import { PeriodLayout } from "./periodLayout";
import { useMemo, useState } from "react";
import { nanoid } from "@reduxjs/toolkit";
import isEqual from "fast-deep-equal";
import {
  Autocomplete,
  Button,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTenantTranslation } from "hooks/formatters";
import { PeriodRuleControls } from "./PeriodRuleControls";
import {
  allPeriods,
  PeriodMaterialExclusivity,
  RulePeriodSelector,
} from "./ruleParameters";

interface ExclusivityRow {
  id: string;
  ruleId: string;
  periods: RulePeriodSelector;
  materials: string[];
}

export const ExclusivityRulesEditor = ({
  periodLayout,
  rules,
  materials,
  onChange,
  disabled,
}: {
  periodLayout: PeriodLayout;
  rules: PeriodMaterialExclusivity[];
  materials: string[];
  onChange: (rules: PeriodMaterialExclusivity[]) => void;
  disabled: boolean;
}) => {
  const { t } = useTenantTranslation();
  const inheritedRows = useMemo(
    () =>
      rules.flatMap((rule) => {
        const ruleId = nanoid();
        return rule.groups.map((materials) => ({
          id: nanoid(),
          ruleId,
          periods: rule.periods,
          materials,
        }));
      }),
    [rules]
  );
  const [snapshot, setSnapshot] = useState<{
    rules: PeriodMaterialExclusivity[];
    rows: ExclusivityRow[];
  } | null>(null);
  const rows =
    snapshot && isEqual(snapshot.rules, rules) ? snapshot.rows : inheritedRows;
  const update = (rows: ExclusivityRow[]) => {
    const grouped = rows.reduce((grouped, row) => {
      const key = `${row.ruleId}/${String(row.periods.is_deployable)}/${row.periods.position}`;
      return new Map([
        ...grouped,
        [
          key,
          {
            periods: row.periods,
            groups: [...(grouped.get(key)?.groups ?? []), row.materials],
          },
        ],
      ]);
    }, new Map<string, PeriodMaterialExclusivity>());
    const rules = [...grouped.values()];
    setSnapshot({ rules, rows });
    onChange(rules);
  };
  return (
    <Stack gap={2}>
      <Typography variant="body2" color="text.secondary">
        {t("autoExclusivityHelp")}
      </Typography>
      {rows.map((row) => (
        <Stack
          key={row.id}
          role="group"
          aria-label={t("autoExclusiveMaterials")}
          direction={{ xs: "column", md: "row" }}
          gap={1}
          alignItems={{ md: "flex-start" }}
        >
          <Autocomplete
            multiple
            size="small"
            fullWidth
            options={[...new Set([...materials, ...row.materials])]}
            value={row.materials}
            disabled={disabled}
            getOptionLabel={(name) => t(name)}
            onChange={(_, materials) => {
              update(
                rows.map((previous) =>
                  previous.id === row.id ? { ...previous, materials } : previous
                )
              );
            }}
            renderInput={(props) => (
              <TextField
                {...props}
                label={t("autoExclusiveMaterials")}
                error={row.materials.length < 2}
              />
            )}
          />
          <PeriodRuleControls
            periodLayout={periodLayout}
            selector={row.periods}
            disabled={disabled}
            onChange={(periods) => {
              update(
                rows.map((previous) =>
                  previous.id === row.id ? { ...previous, periods } : previous
                )
              );
            }}
          />
          <Button
            disabled={disabled}
            onClick={() => {
              update(rows.filter((previous) => previous.id !== row.id));
            }}
          >
            {t("delete")}
          </Button>
        </Stack>
      ))}
      <Button
        disabled={disabled}
        onClick={() => {
          const id = nanoid();
          update([
            ...rows,
            { id, ruleId: id, periods: allPeriods, materials: [] },
          ]);
        }}
      >
        {t("autoAddExclusivityGroup")}
      </Button>
    </Stack>
  );
};
